import { PagoMiembroFields, pagoMiembroValido, pagoMiembroPayload } from "./PagoMiembroFields";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { AlertError } from "../../utils/componentes-utiles/AlertError";
import { getConceptosContabilidadActivos, getTiposTickets, postEventoUnificado } from "../../services/apis";
import { EVENT_TYPE_LIST, getEventType } from "./eventosUtils";

const getFechaHoy = () => {
	const hoy = new Date();
	const year = hoy.getFullYear();
	const month = String(hoy.getMonth() + 1).padStart(2, "0");
	const day = String(hoy.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
};

const formatTimeInputValue = (date) => {
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");

	return `${hours}:${minutes}`;
};

const getHorasPorDefecto = () => {
	const inicio = new Date();
	const final = new Date(inicio.getTime() + 30 * 60 * 1000);

	return {
		hora_inicio: formatTimeInputValue(inicio),
		hora_final: formatTimeInputValue(final),
	};
};

export const CrearEventoDialog = ({ open, onClose, opTrabajadores = [], opRopa = [], onCreated, fechaInicial }) => {
	const createInitialForm = useCallback(() => ({
		id_tipo_evento: 1,
		miembro_paga: 0,
		precio_miembro: "",
		asunto: "",
		descripcion: "",
		fecha: fechaInicial || getFechaHoy(),
		...getHorasPorDefecto(),
		usuarios_encargados: [],
		asistentes_limpieza: [],
		ropa: null,
		precios_tickets: {},
		id_apartado: "",
		invitados_penya: 0,
	}), [fechaInicial]);

	const initialForm = useMemo(() => createInitialForm(), [createInitialForm]);
	const [form, setForm] = useState(initialForm);
	const [guardando, setGuardando] = useState(false);
	const [error, setError] = useState(false);
	const [mensajeError, setMensajeError] = useState("Falta omplir algun camp obligatori");
	const [tiposTicketsPagan, setTiposTicketsPagan] = useState([]);
	const [conceptosContables, setConceptosContables] = useState([]);
	const tipoSeleccionado = getEventType(form.id_tipo_evento);
	const TipoIcon = tipoSeleccionado.icon;
	const esRopa = Number(form.id_tipo_evento) === 4;
	const esLimpieza = Number(form.id_tipo_evento) === 2;
	const esEventoConDetalle = !esRopa && !esLimpieza;
	const usaPreciosTickets = Number(form.id_tipo_evento) === 1;
	const requiereEncargado = !esRopa;

	const handleClose = () => {
		if (guardando) {
			return;
		}

		setForm(createInitialForm());
		onClose();
	};

	const updateField = (field, value) => {
		setForm((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	useEffect(() => {
		if (!open) return;
		if (typeof getConceptosContabilidadActivos === "function") {
			getConceptosContabilidadActivos().then((response) => setConceptosContables(response?.data || []));
		}
	}, [open]);

	useEffect(() => {
		if (open) {
			setForm(createInitialForm());
		}
	}, [createInitialForm, open]);

	useEffect(() => {
		const cargarTiposTickets = async () => {
			if (!open || !usaPreciosTickets) {
				setTiposTicketsPagan([]);
				return;
			}

			const anyo = Number(String(form.fecha || "").slice(0, 4)) || new Date().getFullYear();
			try {
				const response = await getTiposTickets(1, anyo);
				const tiposPagan = (response?.data || []).filter((tipo) => Number(tipo.paga) === 1);
				setTiposTicketsPagan(tiposPagan);
				setForm((prev) => {
					const precios = { ...prev.precios_tickets };
					tiposPagan.forEach((tipo) => {
						if (precios[Number(tipo.id)] === undefined) {
							precios[Number(tipo.id)] = "";
						}
					});

					return {
						...prev,
						precios_tickets: precios,
					};
				});
			} catch (err) {
				console.error("Error en carregar tipus de tiquets:", err);
				setTiposTicketsPagan([]);
			}
		};

		cargarTiposTickets();
	}, [open, usaPreciosTickets, form.fecha]);

	const updatePrecioTicket = (idTipoTicket, value) => {
		setForm((prev) => ({
			...prev,
			precios_tickets: {
				...prev.precios_tickets,
				[Number(idTipoTicket)]: value,
			},
		}));
	};

	const preciosTicketsPayload = tiposTicketsPagan.map((tipo) => ({
		id_tipo_ticket: Number(tipo.id),
		precio: Number(form.precios_tickets[Number(tipo.id)] || 0),
	}));

	const validar = () => Boolean(
		form.id_tipo_evento
		&& form.fecha
		&& form.hora_inicio
		&& (!esEventoConDetalle || form.asunto.trim())
		&& (!requiereEncargado || form.usuarios_encargados.length > 0)
		&& (!esRopa || form.ropa?.value)
	);

	const crearEvento = async () => {
		if (!validar() || !pagoMiembroValido(form)) {
			setMensajeError("Falta omplir algun camp obligatori");
			setError(true);
			return;
		}

		setGuardando(true);
		try {
			const response = await postEventoUnificado({
				...pagoMiembroPayload(form),
				id_tipo_evento: form.id_tipo_evento,
				asunto: esRopa ? form.ropa.label : (esLimpieza ? "Neteja" : form.asunto.trim()),
				descripcion: esRopa ? form.ropa.label : (esLimpieza ? "Neteja" : (form.descripcion.trim() || form.asunto.trim())),
				fecha: form.fecha,
				hora_inicio: form.hora_inicio,
				hora_final: form.hora_final || null,
				invitados_penya: Number(form.invitados_penya || 0),
				id_usuarios_encargados: form.usuarios_encargados.map((usuario) => usuario.value),
				asistentes_limpieza: esLimpieza
					? form.asistentes_limpieza.map((asistente) => asistente.value)
					: [],
				id_ropa: form.ropa?.value || null,
				precio_persona: usaPreciosTickets ? (preciosTicketsPayload.find((item) => item.precio > 0)?.precio || 0) : 0,
				precios_tickets: usaPreciosTickets ? preciosTicketsPayload : [],
				id_apartado: form.id_apartado || null,
			});

			if (response?.success) {
				setForm(createInitialForm());
				onCreated();
				onClose();
				return;
			}

			setMensajeError(response?.message || response?.error || "No s'ha pogut crear l'esdeveniment");
			setError(true);
		} catch (err) {
			console.error("Error en crear esdeveniment:", err);
			setMensajeError("No s'ha pogut crear l'esdeveniment");
			setError(true);
		} finally {
			setGuardando(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle className="events-create-title">
				<TipoIcon />
				Crear esdeveniment
			</DialogTitle>
			<DialogContent dividers>
				<Grid container spacing={2} className="events-create-form">
					<Grid size={{ xs: 12 }}>
						<Box className={`events-create-section events-create-type-section ${tipoSeleccionado.className}`}>
							<Typography className="events-create-section-title">Tipus</Typography>
							<TextField
								select
								fullWidth
								required
								label="Tipus d'esdeveniment"
								value={form.id_tipo_evento}
								onChange={(event) => {
									const idTipo = Number(event.target.value);
									setForm((prev) => ({
										...prev,
										id_tipo_evento: idTipo,
										miembro_paga: 0,
										precio_miembro: "",
										asunto: idTipo === 2 ? "Neteja" : "",
										descripcion: idTipo === 2 ? "Neteja" : "",
										usuarios_encargados: [],
										asistentes_limpieza: [],
										ropa: null,
										precios_tickets: {},
									}));
								}}
							>
								{EVENT_TYPE_LIST.map((tipo) => (
									<MenuItem key={tipo.id} value={tipo.id}>
										{tipo.label}
									</MenuItem>
								))}
							</TextField>
						</Box>
					</Grid>
					{esEventoConDetalle && (
						<Grid size={{ xs: 12 }}>
							<Box className="events-create-section">
								<Typography className="events-create-section-title">Dades</Typography>
								<TextField
									fullWidth
									required
									label="Nom"
									value={form.asunto}
									onChange={(event) => updateField("asunto", event.target.value)}
									inputProps={{ maxLength: 50 }}
								/>
								<TextField
									fullWidth
									multiline
									minRows={3}
									label="Descripció"
									value={form.descripcion}
									onChange={(event) => updateField("descripcion", event.target.value)}
									inputProps={{ maxLength: 500 }}
								/>
							</Box>
						</Grid>
					)}
					{[1, 3].includes(Number(form.id_tipo_evento)) && <PagoMiembroFields form={form} onChange={updateField} />}
					{[1, 3].includes(Number(form.id_tipo_evento)) &&
						<Grid size={{ xs: 12 }}>
							<TextField select fullWidth label="Concepte comptable" value={form.id_apartado} onChange={(event) => updateField("id_apartado", event.target.value)}>
								<MenuItem value="">Sense assignar</MenuItem>
								{conceptosContables.map((concepte) => <MenuItem key={concepte.id} value={concepte.id}>{concepte.nombre}</MenuItem>)}
							</TextField>
						</Grid>
					}

					{usaPreciosTickets && (
						<Grid size={{ xs: 12 }}>
							<Box className="events-create-section events-create-prices-section">
								<Typography className="events-create-section-title">Preus per tiquet</Typography>
								<Grid container spacing={2}>
									{tiposTicketsPagan.map((tipo) => (
										<Grid key={tipo.id} size={{ xs: 12, sm: 6 }}>
											<TextField
												fullWidth
												label={`Preu ${tipo.nombre}`}
												type="number"
												value={form.precios_tickets[Number(tipo.id)] || ""}
												onChange={(event) => updatePrecioTicket(tipo.id, event.target.value)}
												inputProps={{ min: 0, step: "0.01" }}
											/>
										</Grid>
									))}
								</Grid>
							</Box>
						</Grid>
					)}
					{esRopa && (
						<Grid size={{ xs: 12 }}>
							<Box className="events-create-section events-type-clothes">
								<Typography className="events-create-section-title">Roba</Typography>
								<Autocomplete
									fullWidth
									options={opRopa}
									value={form.ropa}
									onChange={(event, value) => updateField("ropa", value)}
									isOptionEqualToValue={(option, value) => option.value === value.value}
									renderOption={(props, option) => (
										<Box component="li" {...props} className="events-clothes-option">
											<span className="events-clothes-dot" style={{ backgroundColor: option.color }} />
											{option.label}
										</Box>
									)}
									renderInput={(params) => (
										<TextField {...params} required label="Roba" />
									)}
								/>
							</Box>
						</Grid>
					)}
					<Grid size={{ xs: 12 }}>
						<Box className="events-create-section">
							<Typography className="events-create-section-title">Data i hora</Typography>
							<Grid container spacing={2}>
								<Grid size={{ xs: 12, sm: 3 }}>
									<TextField
										fullWidth
										required
										label="Data"
										type="date"
										value={form.fecha}
										onChange={(event) => updateField("fecha", event.target.value)}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 3 }}>
									<TextField
										fullWidth
										required
										label="Hora d'inici"
										type="time"
										value={form.hora_inicio}
										onChange={(event) => updateField("hora_inicio", event.target.value)}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 3 }}>
									<TextField
										fullWidth
										label="Hora final"
										type="time"
										value={form.hora_final}
										onChange={(event) => updateField("hora_final", event.target.value)}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 3 }}>
									<TextField fullWidth type="number" label="CONVIDAT GE." value={form.invitados_penya} onChange={(event) => updateField("invitados_penya", Math.max(0, parseInt(event.target.value, 10) || 0))} inputProps={{ min: 0 }} />
								</Grid>
							</Grid>
						</Box>
					</Grid>
					{requiereEncargado && (
						<Grid size={{ xs: 12 }}>
							<Box className="events-create-section">
								<Typography className="events-create-section-title">Organització</Typography>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12 }}>
										<Autocomplete
											multiple
											disableCloseOnSelect
											fullWidth
											options={opTrabajadores}
											value={form.usuarios_encargados}
											onChange={(event, value) => updateField("usuarios_encargados", value)}
											isOptionEqualToValue={(option, value) => option.value === value.value}
											renderInput={(params) => (
												<TextField {...params} required label="Usuaris encarregats" />
											)}
										/>
									</Grid>
									{esLimpieza && (
										<Grid size={{ xs: 12 }}>
											<Autocomplete
												multiple
												disableCloseOnSelect
												fullWidth
												options={opTrabajadores}
												value={form.asistentes_limpieza}
												onChange={(event, value) => updateField("asistentes_limpieza", value)}
												isOptionEqualToValue={(option, value) => option.value === value.value}
												renderInput={(params) => (
													<TextField {...params} label="Assistents inicials" helperText="Podran apuntar-se mes persones despres" />
												)}
											/>
										</Grid>
									)}
								</Grid>
							</Box>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={guardando}>
					Cancel·lar
				</Button>
				<Button variant="contained" startIcon={<AddIcon />} onClick={crearEvento} disabled={guardando}>
					{guardando ? "Creant..." : "Crear esdeveniment"}
				</Button>
			</DialogActions>
			{error && (
				<AlertError open={error} setOpen={setError} titulo="Error" mensaje={mensajeError} />
			)}
		</Dialog>
	);
};
